/**
 * Dropbox Integration Setup
 *
 * Displayed in the Integrations tab. Handles:
 * - Connect/disconnect OAuth flow (full-page redirect — backend handles PKCE server-side)
 * - Connection status display (display name, email, folder, last sync)
 * - Editable target folder path
 * - OAuth result detection via ?dropbox=connected / ?dropbox=error query params
 */
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, RefreshCw, Unplug, FolderOpen, Pencil, Check, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  useDropboxStatus,
  useDropboxConnect,
  useDropboxDisconnect,
  useDropboxSyncAll,
  useUpdateDropboxFolder,
} from '@/hooks/useDropbox';

export function DropboxSetup() {
  const { data: status, isLoading } = useDropboxStatus();
  const connectMutation = useDropboxConnect();
  const disconnectMutation = useDropboxDisconnect();
  const syncAllMutation = useDropboxSyncAll();
  const updateFolderMutation = useUpdateDropboxFolder();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isEditingFolder, setIsEditingFolder] = useState(false);
  const [folderInput, setFolderInput] = useState('');

  // Detect OAuth result on mount via query params set by backend redirect
  useEffect(() => {
    const dropboxParam = searchParams.get('dropbox');
    if (!dropboxParam) return;

    if (dropboxParam === 'connected') {
      toast.success('Dropbox connected successfully');
      queryClient.invalidateQueries({ queryKey: ['dropbox'] });
    } else if (dropboxParam === 'error') {
      toast.error('Failed to connect Dropbox. Please try again.');
    }

    // Clean the param from the URL
    const next = new URLSearchParams(searchParams);
    next.delete('dropbox');
    setSearchParams(next, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnect = async () => {
    try {
      const result = await connectMutation.mutateAsync();
      if (result?.auth_url) {
        window.location.href = result.auth_url;
      }
    } catch {
      // error already shown by mutation onError
    }
  };

  const handleDisconnect = () => {
    disconnectMutation.mutate();
    setShowDisconnectConfirm(false);
  };

  const handleEditFolder = () => {
    setFolderInput(status?.target_folder || '/BuildGravity Invoices');
    setIsEditingFolder(true);
  };

  const handleSaveFolder = () => {
    const trimmed = folderInput.trim();
    if (!trimmed) return;
    updateFolderMutation.mutate(trimmed, {
      onSuccess: () => setIsEditingFolder(false),
    });
  };

  const handleCancelFolder = () => {
    setIsEditingFolder(false);
    setFolderInput('');
  };

  if (isLoading) {
    return (
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading Dropbox status...
        </div>
      </div>
    );
  }

  const isConnected = status?.connected;
  const isConnecting = connectMutation.isPending;

  return (
    <div className="border border-gray-200 rounded-lg p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            {/* Dropbox logo mark */}
            <svg className="w-5 h-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M6 2L1 5.5 6 9l5-3.5L6 2zM18 2l-5 3.5 5 3.5 5-3.5L18 2zM1 12.5L6 16l5-3.5-5-3.5-5 3.5zM13 12.5L18 16l5-3.5-5-3.5-5 3.5zM6 17.5L11 21l5-3.5-5-3.5-5 3.5z" />
            </svg>
            Dropbox
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Automatically save invoice PDFs to a Dropbox folder when sent or downloaded.
          </p>
        </div>

        {isConnected && (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Connected
          </span>
        )}
      </div>

      {/* Connecting state */}
      {isConnecting && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600 shrink-0" />
          <span className="text-sm text-blue-800">Opening Dropbox...</span>
        </div>
      )}

      {/* Connected state */}
      {isConnected && !isConnecting && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {status?.display_name && (
              <div>
                <span className="text-gray-500">Name:</span>{' '}
                <span className="font-medium text-gray-900">{status.display_name}</span>
              </div>
            )}
            {status?.email && (
              <div>
                <span className="text-gray-500">Account:</span>{' '}
                <span className="font-medium text-gray-900">{status.email}</span>
              </div>
            )}
            <div>
              <span className="text-gray-500">Last synced:</span>{' '}
              <span className="text-gray-900">
                {status?.last_sync_at
                  ? new Date(status.last_sync_at).toLocaleString()
                  : 'Never'}
              </span>
            </div>
          </div>

          {/* Target folder (editable) */}
          <div className="pt-1">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5">
              Target Folder
            </p>
            {isEditingFolder ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={folderInput}
                  onChange={(e) => setFolderInput(e.target.value)}
                  placeholder="/BuildGravity Invoices"
                  className="flex-1 text-sm border border-gray-300 rounded px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveFolder();
                    if (e.key === 'Escape') handleCancelFolder();
                  }}
                  autoFocus
                />
                <button
                  onClick={handleSaveFolder}
                  disabled={updateFolderMutation.isPending}
                  className="p-1.5 text-green-700 hover:bg-green-100 rounded disabled:opacity-50"
                  title="Save"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={handleCancelFolder}
                  className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                  title="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <FolderOpen className="w-4 h-4 text-blue-500 shrink-0" />
                <span className="text-sm font-mono text-gray-800">
                  {status?.target_folder || '/BuildGravity Invoices'}
                </span>
                <button
                  onClick={handleEditFolder}
                  className="p-1 text-gray-400 hover:text-gray-700 rounded"
                  title="Edit folder"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Last sync error warning */}
          {status?.last_sync_error && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <span className="text-sm text-amber-800">
                Last sync failed: {status.last_sync_error}. Try resending an invoice or check your connection.
              </span>
            </div>
          )}

          {/* Sync All — hidden for now. Bulk sync causes Dropbox 429 rate limits.
              New invoices sync automatically on send. Manual backfill can be done
              via the API endpoint if needed: POST /api/integrations/dropbox/sync-all */}


          {/* Disconnect */}
          {showDisconnectConfirm ? (
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-blue-200">
              <span className="text-sm text-gray-700">
                Are you sure? This won't delete your Dropbox files.
              </span>
              <button
                onClick={handleDisconnect}
                disabled={disconnectMutation.isPending}
                className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 disabled:opacity-50"
              >
                {disconnectMutation.isPending ? 'Disconnecting...' : 'Yes, disconnect'}
              </button>
              <button
                onClick={() => setShowDisconnectConfirm(false)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowDisconnectConfirm(true)}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors pt-2 border-t border-blue-200"
            >
              <Unplug className="w-3.5 h-3.5" />
              Disconnect
            </button>
          )}
        </div>
      )}

      {/* Not connected state */}
      {!isConnected && !isConnecting && (
        <div className="space-y-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-2">
            <p className="font-medium">How it works:</p>
            <ol className="list-decimal list-inside space-y-1 text-gray-600">
              <li>Click "Connect Dropbox" below</li>
              <li>Sign in to your Dropbox account</li>
              <li>Authorize access</li>
              <li>Invoice PDFs will be saved to your chosen folder when sent or downloaded</li>
            </ol>
          </div>

          <button
            onClick={handleConnect}
            disabled={isConnecting}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isConnecting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M6 2L1 5.5 6 9l5-3.5L6 2zM18 2l-5 3.5 5 3.5 5-3.5L18 2zM1 12.5L6 16l5-3.5-5-3.5-5 3.5zM13 12.5L18 16l5-3.5-5-3.5-5 3.5zM6 17.5L11 21l5-3.5-5-3.5-5 3.5z" />
                </svg>
                Connect Dropbox
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
